import produce from 'immer'
import { assign, filter, forEach, get, isEmpty, map, set, uniqBy } from 'lodash'

import { METADATA_TYPES } from '../../../common/constants'
import { buildTreeHierarchy, transformTree } from '../../../common/helpers'

import {
  ADD_AWARD,
  DELETE_AWARD,
  EXPAND_AWARD_MAIN_TREE_KEYS,
  EXPAND_AWARD_SUPPORTING_TREE_KEYS,
  FETCH_AWARDS,
  MOVE_AWARDS,
  RENAME_AWARD,
  RESET_AWARD_TREE,
  SAVE_AWARDS,
  SET_DATA_FOR_RENAMING_AWARD,
  SET_IS_CURATION_ENABLED_AWARD,
} from '../action-types'
import { AWARDS_INITIAL_STATE } from '../initial-states'

const awardsReducer = (state = AWARDS_INITIAL_STATE, action) => {
  switch (action.type) {
    case FETCH_AWARDS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_AWARDS.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.awards

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.AWARDS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          latestEvent,
        })
      })

    case FETCH_AWARDS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          initialData: [],
          data: [],
          latestEvent: undefined,
        }),
      )

    case FETCH_AWARDS.CLEANUP:
      return { ...AWARDS_INITIAL_STATE }

    case SAVE_AWARDS.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
          latestEvent: undefined,
        }),
      )

    case SAVE_AWARDS.COMPLETE:
      return produce(state, nextState => {
        const { results, latestEvent } = action.payload.data.saveAwards

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.AWARDS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
          latestEvent,
        })
      })

    case SAVE_AWARDS.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
          latestEvent: undefined,
        }),
      )

    case DELETE_AWARD.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case DELETE_AWARD.COMPLETE:
      return produce(state, nextState => {
        const { results } = action.payload.data.deleteAward

        const treeData = buildTreeHierarchy(results, METADATA_TYPES.AWARDS)

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case DELETE_AWARD.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case SET_IS_CURATION_ENABLED_AWARD.START:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: true,
          error: undefined,
        }),
      )

    case SET_IS_CURATION_ENABLED_AWARD.COMPLETE:
      return produce(state, nextState => {
        const updatedAward = action.payload.data.setIsCurationEnabledAward

        const treeData = transformTree(nextState.initialData, node =>
          node.id === updatedAward.id
            ? {
                ...node,
                isCurationEnabled: updatedAward.isCurationEnabled,
              }
            : node,
        )

        return assign(nextState, {
          isLoading: false,
          error: undefined,
          initialData: treeData,
          data: treeData,
          isDirty: false,
          dataForRenaming: undefined,
        })
      })

    case SET_IS_CURATION_ENABLED_AWARD.FAIL:
      return produce(state, nextState =>
        assign(nextState, {
          isLoading: false,
          error: { message: action.error.message },
        }),
      )

    case ADD_AWARD:
      return produce(state, nextState => {
        const { parentPath } = action.payload
        const isRootNodeAdded = isEmpty(parentPath)

        const arrayToAddNodeTo = isRootNodeAdded
          ? nextState.data
          : get(nextState.data, `${parentPath}.children`, [])

        arrayToAddNodeTo.push(action.payload)

        const treeDataWithNewNode = isRootNodeAdded
          ? arrayToAddNodeTo
          : set(nextState.data, `${parentPath}.children`, arrayToAddNodeTo)

        return assign(nextState, {
          ...nextState,
          data: treeDataWithNewNode,
          isDirty: true,
          dataForRenaming: { id: action.payload.id },
        })
      })

    case RENAME_AWARD:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: set(
            nextState.data,
            `${action.payload.path}.name`,
            action.payload.name,
          ),
          isDirty: true,
        }),
      )

    case SET_DATA_FOR_RENAMING_AWARD:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          dataForRenaming: action.payload,
        }),
      )

    case MOVE_AWARDS:
      return produce(state, nextState => {
        const { nodesToMove, destinationNode } = action.payload
        let newTreeData = nextState.data

        // remove nodes to be moved
        forEach(nodesToMove, ({ id: idToMove, parentPath }) => {
          const isMovingRootNode = isEmpty(parentPath)

          if (isMovingRootNode) {
            newTreeData = filter(newTreeData, ({ id }) => id !== idToMove)
          } else {
            newTreeData = set(
              newTreeData,
              `${parentPath}.children`,
              filter(
                get(newTreeData, `${parentPath}.children`),
                ({ id }) => id !== idToMove,
              ),
            )
          }
        })

        const isMovedToRoot = destinationNode.level === 1

        if (isMovedToRoot) {
          newTreeData = [
            ...newTreeData,
            ...map(nodesToMove, node => ({
              ...node,
              parentId: null,
              level: 1,
              idPath: [node.id],
              namePath: [node.name],
            })),
          ]
        } else {
          newTreeData = transformTree(newTreeData, node =>
            node.id === destinationNode.id
              ? {
                  ...node,
                  children: uniqBy(
                    [
                      ...(node.children || []),
                      ...map(nodesToMove, node => ({
                        ...node,
                        parentId: destinationNode.id,
                        level: destinationNode.level - 1,
                        idPath: [...destinationNode.idPath, node.id],
                        namePath: [...destinationNode.namePath, node.name],
                      })),
                    ],
                    'id',
                  ),
                }
              : node,
          )
        }

        return assign(nextState, {
          ...nextState,
          isDirty: true,
          data: buildTreeHierarchy(newTreeData, METADATA_TYPES.AWARDS),
        })
      })

    case EXPAND_AWARD_MAIN_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          mainTreeExpandedKeys: action.payload,
        }),
      )

    case EXPAND_AWARD_SUPPORTING_TREE_KEYS:
      return produce(state, nextState =>
        assign(nextState, {
          supportingTreeExpandedKeys: action.payload,
        }),
      )

    case RESET_AWARD_TREE:
      return produce(state, nextState =>
        assign(nextState, {
          ...nextState,
          data: nextState.initialData,
          isDirty: false,
          dataForRenaming: undefined,
        }),
      )

    default:
      return state
  }
}

export default awardsReducer
